// packages
import React from "react";
import { z } from "zod";
import { BuilderContent } from "@builder.io/sdk-react";
import isEqual from "react-fast-compare";
import { DateTime } from "luxon";
import { isAxiosError } from "axios";
import { QueryClient } from "@tanstack/react-query";
import { FieldValues, UseFormSetValue } from "react-hook-form";

// context
import { AppStateInterface } from "@/shared/contexts/AppLayer";

// types
import { CaQuoteFormSteps } from "./formConfigTypes";
import { FormConfig, FormStep } from "@/shared/types/Form";
import { PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";
import { CoverageSettings, PartialQuoteSchema, Quote } from "@/shared/types/Quote.interface";
import { CaCheckoutStepSchema, CaPetPoliciesSchema, PolicyStepSchema } from "./src/schema/PtzCaQuote";

// components
import SiteHeader from "@/shared/components/SiteHeader";
import SiteFooter from "@/shared/components/SiteFooter";
import { PriceInfo } from "@/shared/components/PriceInfo";
import FooterContent from "./src/components/FooterContent";
import { StepperConfig } from "@/shared/components/Stepper";
import { CoverageEditor } from "@/shared/components/CoverageEditor";
import { CustomizationSlot } from "@/shared/components/CustomizationSlot";
import { UserContactEditor } from "@/shared/components/UserContactEditor";
import ContactConsentDisclaimer from "@/quote-ptz-ca/src/components/ContactConsentDisclaimer";
import { BillingEditor } from "@/quote-ptz-ca/src/components/BillingEditor";
import { CanadaUnderwriterContent } from "@/quote-ptz-ca/src/components/CanadaUnderwriterContent";

// utils
import { cn } from "@/shared/utils";
import { UIUtils } from "@/shared/utils/UIUtils";
import ErrorUtils from "@/shared/utils/ErrorUtils";
import { QuoteAPI } from "@/shared/utils/QuoteAPI";
import { PublicConfig } from "@/shared/PublicConfig";
import { EmailUtils } from "@/shared/utils/EmailUtils";
import Strings from "@/shared/utils/Strings.constants";
import { CookieUtils } from "@/shared/utils/CookieUtils";
import { ADD_PET_MODAL_TRIGGERS, TOP_BREED_PATTERNS_CA } from "@/shared/utils/constants";
import { BuilderPartner, BuilderUtils } from "@/shared/utils/BuilderUtils";
import { PtzCaDataUtils } from "./src/utils/PtzCaDataUtils";
import { CoverageUtils } from "@/shared/utils/CoverageUtils";
import { TrackingUtils } from "@/shared/utils/TrackingUtils";
import { AnalyticsUtils } from "@/shared/utils/AnalyticsUtils";
import { QuoteDataUtils } from "@/shared/utils/QuoteDataUtils";
import { PresetCoverageLevel } from "@/shared/utils/CoverageUtils";
import { CA_FOOTER_LINKS, CA_MAX_AGE_CAT, CA_MAX_AGE_DOG, SAMPLE_POLICY_URL } from "./src/utils/constants";

// media
import Image from "next/image";
import { SpotLogo } from "@/shared/media/SpotLogo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart, faPlus } from "@fortawesome/pro-regular-svg-icons";
import CatAndDog from "@/shared/media/images/cat-and-dog.png";
import { PetInfoWrapper } from "@/shared/components/PetInfoWrapper";
import PolicyCTA from "@/shared/components/PolicyCTA";
import { PolicyErrorController } from "@/shared/components/PolicyErrorController";
import UnderwriterModal from "@/shared/components/UnderwriterModal";
import { Button } from "@/shared/components/ui";
import Exclusions from "@/shared/components/Exclusions";
import PrettyStepOne from "./src/components/PrettyStepOne";

type CaQuoteFormProps = {
    underwriter: PetUnderwriterType;
    updateAppState: (state: Partial<AppStateInterface>) => void;
    updateQuote?: UseFormSetValue<Quote>;
    isUpdating: boolean;
    queryClient: QueryClient;
    quoteId?: string;
    setQuoteId: (quoteId: string) => void;
    currentStep: CaQuoteFormStepIds;
    priorityCode?: string;
    updateCurrentStep: (stepId: CaQuoteFormStepIds, quoteId?: string) => void;
    builderPartnerData?: BuilderPartner;
    showStepLabel: boolean;
};

const FORM_ID = PublicConfig.PTZ_CA.FORM_ID;
export type CaQuoteFormStepIds = "pets" | "coverage" | "billing";

export const getCaQuoteForm = ({
    underwriter,
    updateAppState,
    isUpdating,
    queryClient,
    quoteId,
    setQuoteId,
    currentStep,
    priorityCode,
    updateCurrentStep,
    updateQuote,
    builderPartnerData,
    showStepLabel
}: CaQuoteFormProps): FormConfig<CaQuoteFormStepIds, z.infer<typeof PartialQuoteSchema>> => {
    const quoteApi = new QuoteAPI(underwriter);
    const builderUtils = new BuilderUtils(underwriter);
    const initialBuilderContent = queryClient.getQueryData(["builder-ssr-content", "retargeting-form-copy"]) as BuilderContent | undefined;
    // Co-brand: the SSR prefetch already fetched this entry; SiteHeader only mounts the slot when it is handed in
    // (same wiring as quote-ptz-us). Without it every `formId is quote-ptz-ca` co-brand entry silently never renders.
    const initialCoBrandContent = queryClient.getQueryData(["builder-ssr-content", "co-brand"]) as BuilderContent | undefined;
    const COOKIE_ATTRIBUTES_KEY = PublicConfig.PTZ_CA.COOKIE_QUOTE_ATTRIBUTES_KEY;

    const emailUtils = new EmailUtils(underwriter, queryClient);

    const stepsConfig: FormStep<z.infer<typeof PartialQuoteSchema>, CaQuoteFormStepIds>[] = [
        {
            id: CaQuoteFormSteps.PETS.id,
            stepLabel: CaQuoteFormSteps.PETS.label,
            hideStepper: true,
            hideContinueButton: true,
            hideCtaRow: true,
            stepSchema: PolicyStepSchema,
            getInitialValues: quote => PtzCaDataUtils.getPolicyStepInitialValues(quote),
            disclaimerContent: props => {
                return (
                    <>
                        <ContactConsentDisclaimer ctaText={builderPartnerData?.customSubmitLabel ?? Strings.POLICY_NEXT_STEP_CTA} />
                    </>
                );
            },
            continueButtonTitle: builderPartnerData?.customSubmitLabel ?? Strings.PTZ_CA.SELECT_COVERAGE_TEXT,
            continueButtonId: `${FORM_ID}|${Strings.DATA_DOG.POLICY_STEP_CTA}`,

            allowSubmit: () => !isUpdating,
            allowContinue: async values => {
                if (!values) return false;
                updateAppState({ asyncErrors: undefined, isQuoteUpdating: true });
                const isNewQuote = !values?.id;
                const currentQuote = queryClient.getQueryData(["quote", values.id]) as Quote | undefined;
                const hasQuoteChanged = !isEqual(currentQuote, values);

                // CA plan ids are province-specific. When the postal changes, the underwriter nulls the
                // selected coverage — losing the customer's level. Stash the current selection (still in
                // `values` here, before the PUT) so the Coverage step can re-map it to the new province's
                // equivalent plan instead of reverting to the default.
                const postalChanged = UIUtils.normalizePostalCode(values.ratingZipcode) !== UIUtils.normalizePostalCode(currentQuote?.ratingZipcode);
                if (postalChanged) {
                    const preserved: Record<string, CoverageSettings> = {};
                    values.policies?.forEach(policy => {
                        if (policy.id && policy.coverageSettings?.coverages?.length) {
                            preserved[policy.id] = policy.coverageSettings;
                        }
                    });
                    updateAppState({ preservedCoverageByPet: Object.keys(preserved).length ? preserved : null });
                }

                // If this is a new quote, or any of the form values have changed
                const emailInQuote = currentQuote?.email;
                const shouldCheckEmail = !values?.id || (!!values.email && emailInQuote !== values.email);

                const cookieId = PublicConfig.PTZ_CA.COOKIE_QUOTE_ID_KEY;
                const existingCookieQuoteId = CookieUtils.get(cookieId);

                if (shouldCheckEmail && !!values.email) {
                    const tasks: Promise<boolean>[] = [];

                    // TODO: verify endpoint not working for CA

                    tasks.push(
                        emailUtils.handleEmailDebounce({
                            email: values.email,
                            debounceEntries: values?.extra?.debounce,
                            updateAppState
                        })
                    );

                    const results = await Promise.all(tasks);

                    if (results.some(result => result === false)) {
                        return false;
                    }
                }

                if (isNewQuote || hasQuoteChanged) {
                    try {
                        const policyData = QuoteDataUtils.quoteToPetQuote({ ...values, lastStepID: "coverage" });

                        const updatedPetQuote = await quoteApi.updateQuote(policyData);

                        if (!updatedPetQuote?.quoteId) {
                            throw new Error("No quote ID in response");
                        }
                        const updatedQuote = QuoteDataUtils.petQuoteToQuote(updatedPetQuote, underwriter, {
                            includeBirthProps: true,
                            formatRatingZipcode: PtzCaDataUtils.formatRatingZipcode,
                            showNetPrice: PublicConfig.PTZ_CA.SHOW_NET_PRICE
                        });
                        queryClient.setQueryData<Quote>(["quote", updatedPetQuote.quoteId], updatedQuote);
                        setQuoteId(updatedPetQuote.quoteId);

                        const quoteProperties = TrackingUtils.buildTrackingPayload(updatedPetQuote);
                        if (isNewQuote) {
                            AnalyticsUtils?.trackSegmentEvent(`Checkout Started`, quoteProperties);
                        } else {
                            AnalyticsUtils?.trackSegmentEvent(`Order Updated`, quoteProperties);
                        }

                        updateAppState({ hasUnknownError: false, isQuoteUpdating: false });
                        if (existingCookieQuoteId !== updatedPetQuote.quoteId) {
                            CookieUtils.set(cookieId, updatedPetQuote.quoteId, { expires: 365 });
                        }
                        BuilderUtils.writeQuoteAttributesCookie(updatedQuote, COOKIE_ATTRIBUTES_KEY);
                        return updatedQuote;
                    } catch (error) {
                        if (isAxiosError(error)) {
                            const errorStatus = error?.response?.status ?? 0;
                            if (ErrorUtils.isSpotApiError(error.response?.data)) {
                                const asyncErrors = ErrorUtils.getErrorIds(error?.response?.data);
                                updateAppState({ asyncErrors, isQuoteUpdating: false });
                                return false;
                            }
                            // For unhandled errors, throw a generic "unknown" error to be caught by the global error handler (ex: appState.hasUnknownError)
                            if (errorStatus >= 400) {
                                updateAppState({ hasUnknownError: true, isQuoteUpdating: false });
                                return false;
                            }
                        }
                        return false;
                    }
                } else {
                    updateAppState({ hasUnknownError: false, isQuoteUpdating: false });
                    if (!values.lastStepID && !!values.id && !!updateQuote) {
                        await quoteApi
                            .setScratchPadValue(values.id, "extra", { quote: { lastStepID: "coverage", lastStepIDUpdatedAt: DateTime.utc().toISO() } })
                            .then(() => queryClient.invalidateQueries({ queryKey: ["quote"] }));
                        updateQuote("lastStepID", "coverage");
                        if (existingCookieQuoteId !== values.id) {
                            CookieUtils.set(cookieId, values.id, { expires: 365 });
                        }
                    }
                    return values;
                }
            },
            render: props => {
                const handleUpdateData = async (dataArray: { type: keyof (typeof BuilderUtils)["typeToFieldKeyMap"]; data: FieldValues }[]) => {
                    await builderUtils.updateQuoteExtraData({ quote: props.value, newDataArray: dataArray, updateQuote: updateQuote });
                };
                return (
                    <React.Fragment>
                        <div className="grid grid-cols-1 pt-8 lg:grid-cols-2 lg:gap-8 lg:pt-[150px]">
                            <CustomizationSlot
                                type="retargeting-form-copy"
                                fallback={<PrettyStepOne title={`Canada's Trusted Pet Insurance.`} subTitle={`Worried about unexpected vet bills? We’ve got you covered.`} />}
                                data={props.value}
                                formId={FORM_ID}
                                formStepId={CaQuoteFormSteps.PETS.id}
                                formData={props.value?.extra?.formData}
                                updateData={data => builderUtils.updateQuoteExtraData({ quote: props.value, newDataArray: data, updateQuote: updateQuote })}
                                priorityCode={priorityCode}
                                initialContent={initialBuilderContent}
                            />
                            <div className="relative mt-5 sm:mt-8 lg:mt-0">
                                <Image
                                    src={CatAndDog}
                                    alt=""
                                    className="absolute right-4 top-[-94px] w-[160px] sm:top-[-155px] sm:w-[264px] lg:left-1/2 lg:top-[-122px] lg:w-[210px] lg:-translate-x-1/2"
                                />
                                <div className="rounded-3xl bg-background-primary p-3 shadow-md sm:p-8">
                                    <div className="mb-4 grid grid-cols-1 gap-1 sm:grid-cols-2">
                                        <h2 className="h4 flex items-center justify-center text-lg font-bold sm:justify-start">{Strings.TELL_US_PET}</h2>
                                        <div className="flex items-center justify-center gap-1 text-xs sm:justify-end">
                                            <FontAwesomeIcon icon={faHeart} className="size-4 text-content-brand" />
                                            {!!quoteId ? Strings.ADD_EDIT_PETS_LATER : Strings.ADD_MULTIPLE_PETS_LATER}
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-6">
                                        <PolicyErrorController errors={{ "invalid-postal-code": Strings.ERRORS.POSTAL_CODE_INVALID }} />
                                        <PetInfoWrapper
                                            firstPetOnly
                                            petInfoConfigProps={{
                                                floatingVariant: "floating-inner",
                                                topBreeds: TOP_BREED_PATTERNS_CA,
                                                showBirthDate: true,
                                                showMicrochip: true,
                                                containBreedDropdown: true,
                                                showZipField: true,
                                                zipField: {
                                                    label: Strings.PTZ_CA.POSTAL_CODE,
                                                    placeholder: Strings.PTZ_CA.POSTAL_CODE_PLACEHOLDER,
                                                    mask: UIUtils.maskPostalCodeCA
                                                },
                                                breedField: {
                                                    label: Strings.BREED,
                                                    placeholder: Strings.BREED
                                                },
                                                ageField: {
                                                    maxAgeCat: CA_MAX_AGE_CAT,
                                                    maxAgeDog: CA_MAX_AGE_DOG,
                                                    maxAgeError: `For %{age}+ year-old %{species}s, please call ${UIUtils.formatPhone(Strings.PTZ_CA.PHONE_NUMBER)} for a specialized quote.`
                                                },
                                                nameField: { label: Strings.PETS_NAME },
                                                styles: {
                                                    wrapper: "grid grid-cols-2 gap-4 sm:gap-6",
                                                    fields: {
                                                        name: "order-1 col-span-2",
                                                        birthdate: "flex flex-col xs:flex-row order-2 col-span-2 gap-4 sm:gap-6",
                                                        species: "order-3 col-span-1",
                                                        gender: "order-4 col-span-1",
                                                        zipCode: "order-5 col-span-1",
                                                        microchip: "order-6 col-span-1",
                                                        breed: "order-7 col-span-2"
                                                    }
                                                }
                                            }}
                                        />
                                        <UserContactEditor
                                            config={{
                                                stepSchema: PolicyStepSchema,
                                                showMobilePhone: true,
                                                showEmailDisclaimer: true,
                                                showMobileDisclaimer: true,
                                                underwriter: "ptz-ca",
                                                floatingVariant: "floating-inner",
                                                isExistingQuoteCheckEnabled: PublicConfig.PTZ_CA.QUOTE_SUGGESTION_ENABLED,
                                                // Postal code is rendered in the pet card (above breed) instead; hide it here.
                                                // NOTE: hiding the zip Controller also disables the zip leg of useExistingQuote in CA —
                                                // debouncedZipCode/checkForQuote only update from that Controller's onChange, which no longer
                                                // renders here. Safe today because QUOTE_SUGGESTION is off for CA (no member center / quote modal).
                                                // If CA ever enables it, the pet-card postal field must drive setDebouncedZipCode + checkForQuote. (ENG-16610)
                                                hideZipField: true,
                                                zipField: {
                                                    label: Strings.PTZ_CA.POSTAL_CODE,
                                                    placeholder: Strings.PTZ_CA.POSTAL_CODE_PLACEHOLDER,
                                                    mask: UIUtils.maskPostalCodeCA,
                                                    showGiftCardWrapper: false
                                                },
                                                emailField: {
                                                    label: Strings.EMAIL_ADDRESS,
                                                    placeholder: " "
                                                },
                                                firstNameField: {
                                                    label: Strings.FIRST_NAME,
                                                    placeholder: " "
                                                },
                                                lastNameField: {
                                                    label: Strings.LAST_NAME,
                                                    placeholder: " "
                                                },
                                                phoneField: {
                                                    label: Strings.MOBILE_PHONE_OPTIONAL,
                                                    placeholder: " "
                                                }
                                            }}
                                            styles={{
                                                wrapper: "grid grid-cols-2 gap-4 sm:gap-6",
                                                fields: {
                                                    email: "order-1 col-span-2",
                                                    firstName: "order-2 col-span-1",
                                                    lastName: "order-3 col-span-1",
                                                    phone: "order-4 col-span-2"
                                                }
                                            }}
                                        />
                                        <PolicyCTA formId={FORM_ID} customSubmitLabel={builderPartnerData?.customSubmitLabel} variant={"default-rounded"} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <CustomizationSlot
                            type="above-cta"
                            data={props.value}
                            formId={FORM_ID}
                            formStepId="pets"
                            formData={props.value?.extra?.formData}
                            updateData={handleUpdateData}
                            priorityCode={priorityCode}
                        />
                    </React.Fragment>
                );
            }
        },
        {
            id: CaQuoteFormSteps.COVERAGE.id,
            stepLabel: CaQuoteFormSteps.COVERAGE.label,
            isMaxDeterministicStep: true,
            bottomWidget: props => {
                const { roundedPrice, discountsAmount } = CoverageUtils.getPriceInfoData({ value: props?.value, includeTransactionFee: false, feeType: undefined });

                return <PriceInfo totalPrice={roundedPrice} policiesCount={props?.value?.policies?.length ?? 0} discountsAmount={discountsAmount} variant="inline" />;
            },

            alternateSubmitButton: (
                <Button
                    variant="outline-secondary"
                    size="md"
                    decoratorWrapperClass="lg:shrink-0"
                    className="w-full rounded-full border-2 border-content-brand py-5 text-content-brand focus:rounded-full lg:whitespace-nowrap"
                    startDecorator={<FontAwesomeIcon icon={faPlus} className="size-4" />}
                    onClick={() => updateAppState({ addPetModal: ADD_PET_MODAL_TRIGGERS.ALT_SUBMIT_CTA })}
                >
                    {Strings.PTZ_CA.ADD_PET_GET_DISCOUNT}
                </Button>
            ),

            continueButtonTitle: Strings.PROCEED_TO_CHECKOUT,
            continueButtonClass: "rounded-full focus:rounded-full",
            continueButtonId: `${FORM_ID}|${Strings.DATA_DOG.COVERAGE_STEP_CTA}`,
            disclaimerContent: (
                <div className="mx-auto mb-5 max-w-[976px] text-xs text-content-secondary">
                    By clicking “Proceed to Checkout” you acknowledge that this policy has specific limitations including an annual coverage limit. The actual policy issued Terms
                    and Conditions will prevail. Medical Conditions that are noted, symptomatic or diagnosed prior to enrollment, or during a waiting period are pre-existing to
                    Coverage and not eligible for reimbursement.
                </div>
            ),
            render: props => {
                const coveragePresetData: PresetCoverageLevel[] = [
                    {
                        name: "Basic",
                        config: {
                            type: ["accident", "illness"],
                            annualLimit: PublicConfig.PTZ_CA.DEFAULT_ANNUAL_LIMIT
                        }
                    }
                ];

                const handleUpdateData = async (dataArray: { type: keyof (typeof BuilderUtils)["typeToFieldKeyMap"]; data: FieldValues }[]) => {
                    await builderUtils.updateQuoteExtraData({ quote: props.value, newDataArray: dataArray, updateQuote: updateQuote });
                };

                return (
                    <>
                        <CoverageEditor
                            {...props}
                            editorConfig={{
                                showStickyPriceLabel: false,
                                showNumbered: true,
                                isPreExistingBannerEnabled: false,
                                autoApplyDefaultPlan: true,
                                formId: FORM_ID,
                                coveragePresetData,
                                title: Strings.CREATE_PLAN,
                                showStepLabel,
                                defaultPlanConfig: {
                                    type: ["accident", "illness"],
                                    details: {
                                        annualLimit: {
                                            value: 5000,
                                            symbol: "$"
                                        }
                                    }
                                },
                                includeTransactionFee: false,
                                feeType: undefined,
                                termsInModal: ["annualLimit", "annualDeductible", "reimbursement"],
                                samplePolicyUrl: SAMPLE_POLICY_URL,
                                showTelehealth: false,
                                isDentalCovered: false,
                                isMicrochippingCovered: false,
                                showCompellingStats: false,
                                waitingPeriods: Strings.PTZ_CA.WAITING_PERIODS,
                                preExistingConditions: Strings.PTZ_CA.EXCLUSIONS.PRE_EXISTING_CONDITIONS,
                                preventiveConfig: {
                                    labels: {
                                        basic: Strings.PTZ_CA.PREVENTIVE_BASIC,
                                        advanced: Strings.PTZ_CA.PREVENTIVE_ADVANCED
                                    },
                                    header: Strings.PTZ_CA.PREVENTIVE_CARE_HEADER,
                                    subheader: Strings.PTZ_CA.PREVENTIVE_CARE_SUBHEADER
                                },
                                exclusions: (
                                    <Exclusions
                                        showTelehealth={false}
                                        samplePolicyUrl={SAMPLE_POLICY_URL}
                                        waitingPeriods={Strings.PTZ_CA.WAITING_PERIODS}
                                        preExistingConditions={Strings.PTZ_CA.EXCLUSIONS.PRE_EXISTING_CONDITIONS}
                                    />
                                ),
                                customizationSlot: (
                                    <CustomizationSlot
                                        type="above-cta"
                                        data={props.value}
                                        formId={FORM_ID}
                                        formStepId="coverage"
                                        formData={props.value?.extra?.formData}
                                        updateData={handleUpdateData}
                                        priorityCode={priorityCode}
                                    />
                                ),
                                petModalConfig: {
                                    floatingVariant: "floating-inner",
                                    resolverSchema: CaPetPoliciesSchema,
                                    topBreeds: TOP_BREED_PATTERNS_CA,
                                    showMicrochip: true,
                                    showBirthDate: true,
                                    formStyles: {
                                        wrapper: "grid grid-cols-2 gap-4",
                                        fields: {
                                            name: "order-1 col-span-2",
                                            birthdate: "order-2 flex flex-col xs:flex-row gap-4 col-span-2 lg:flex-col",
                                            species: "order-3 col-span-1",
                                            gender: "order-4 col-span-1",
                                            breed: "order-5 col-span-2",
                                            microchip: "order-6 col-span-2"
                                        }
                                    },
                                    ageField: {
                                        maxAgeCat: CA_MAX_AGE_CAT,
                                        maxAgeDog: CA_MAX_AGE_DOG,
                                        maxAgeError: "For %{age}+ year-old %{species}s, please call 1-888-501-7768 for a specialized quote."
                                    },
                                    breedField: {
                                        label: Strings.BREED
                                    },
                                    badgeLabel: Strings.PTZ_CA.UP_TO_MULTIPET_DISCOUNT,
                                    triggerLabel: Strings.PTZ_CA.ADD_PET_GET_DISCOUNT
                                },
                                getModalInitialValues: QuoteDataUtils.createNewPolicyCA,
                                multiPetVariant: "card",
                                isCategorizedPreventiveEnabled: true,
                                disableOptimisticDiscountPricing: PublicConfig.PTZ_CA.DISABLE_OPTIMISTIC_DISCOUNT_PRICING
                            }}
                        />
                    </>
                );
            },
            hidePreviousSteps: true,
            allowSubmit: () => !isUpdating,
            // Persist lastStepID → "billing"
            allowContinue: async values => {
                if (!values) return false;
                if (!!values.id && values.lastStepID !== CaQuoteFormSteps.BILLING.id && !!updateQuote) {
                    try {
                        await quoteApi
                            .setScratchPadValue(values.id, "extra", { quote: { lastStepID: CaQuoteFormSteps.BILLING.id, lastStepIDUpdatedAt: DateTime.utc().toISO() } })
                            .then(() => queryClient.invalidateQueries({ queryKey: ["quote"] }));
                        updateQuote("lastStepID", CaQuoteFormSteps.BILLING.id);
                    } catch (error) {
                        // Bookkeeping only — don't block the customer from reaching payment over a
                        // failed lastStepID write; resume will just land them on Coverage.
                        console.error("[ca-quote] failed to persist lastStepID=billing", error);
                    }
                }
                return values;
            }
        },
        {
            id: CaQuoteFormSteps.BILLING.id,
            stepLabel: CaQuoteFormSteps.BILLING.label,
            hidePreviousSteps: true,
            // Payment submission is owned by the Chase iframe (its in-frame Pay button), not a step CTA.
            hideContinueButton: true,
            // stepSchema + getInitialValues are required for StepRenderer to wrap the step in an RHF
            // FormProvider — useBillingFrequency's useFormContext() depends on it.
            stepSchema: CaCheckoutStepSchema,
            getInitialValues: quote => ({
                billingInfo: { frequency: quote?.billingInfo?.frequency ?? "monthly" }
            }),
            render: props => <BillingEditor {...props} />,
            disclaimerContent: props => (
                <CustomizationSlot
                    type="above-disclaimer"
                    data={props.value}
                    formId={FORM_ID}
                    formStepId={CaQuoteFormSteps.BILLING.id}
                    formData={props.value?.extra?.formData}
                    updateData={data => builderUtils.updateQuoteExtraData({ quote: props.value, newDataArray: data, updateQuote: updateQuote })}
                    priorityCode={priorityCode}
                />
            )
        }
    ];

    const steps: StepperConfig<CaQuoteFormStepIds>[] = stepsConfig.map(step => ({
        stepValue: step.id as CaQuoteFormStepIds,
        shouldSkip: step.shouldSkip,
        hideStepper: step.hideStepper,
        label: step.stepLabel
    }));

    const customPageClasses = {
        "bg-background-surface-base": true
    };

    return {
        id: FORM_ID,
        schema: PartialQuoteSchema,
        customPageClassName: cn(customPageClasses),
        header: (value, otherValues, isUpdating) => {
            const canadaUnderwriterContent = <CanadaUnderwriterContent />;
            const formattedPartnerPhone = builderPartnerData?.customPhone?.replace(/\D/g, "");
            return (
                <SiteHeader<CaQuoteFormStepIds>
                    quote={value}
                    updateQuote={updateQuote}
                    formID={FORM_ID}
                    underwriter={underwriter}
                    phoneNumber={formattedPartnerPhone ?? Strings.PTZ_CA.PHONE_NUMBER}
                    logo={<SpotLogo />}
                    logoFullWidth={currentStep === CaQuoteFormSteps.PETS.id}
                    currentStep={currentStep}
                    steps={steps}
                    updateCurrentStep={updateCurrentStep}
                    underwriterLink={<UnderwriterModal open={true} content={canadaUnderwriterContent} />}
                    underwriterContent={canadaUnderwriterContent}
                    priorityCode={priorityCode}
                    initialCobrandData={initialCoBrandContent}
                    showStepLabel={showStepLabel}
                />
            );
        },
        steps: stepsConfig,
        footer: (value, otherValues, isUpdating) => {
            const policies = value?.policies ?? [];
            const { roundedPrice, discountsAmount } = CoverageUtils.getPriceInfoData({ value: value, includeTransactionFee: false, feeType: undefined });
            const footerClass = {
                "mt-0": currentStep === CaQuoteFormSteps.PETS.id
            };

            const builderCustomDisclaimers = builderPartnerData?.disclaimers;

            const getPetStepDisclaimers = () => {
                // If builder custom disclaimers exist, use them
                if (builderCustomDisclaimers && builderCustomDisclaimers.length > 0) {
                    return {
                        isVisible: currentStep === CaQuoteFormSteps.PETS.id,
                        disclaimer: (
                            <>
                                {builderCustomDisclaimers.map((item, index) => {
                                    // Clean the URL if it exists by removing invisible characters
                                    const cleanUrl = item.disclaimerLink ? item.disclaimerLink.replace(/[\u200B-\u200D\uFEFF]/g, "") : undefined;

                                    return (
                                        <div key={index}>
                                            <sup>{index + 1}</sup>{" "}
                                            {cleanUrl ? (
                                                <a href={cleanUrl} target="_blank" className="underline">
                                                    {item.disclaimerText}
                                                </a>
                                            ) : (
                                                item.disclaimerText
                                            )}
                                        </div>
                                    );
                                })}
                            </>
                        )
                    };
                }

                // Otherwise, return the default pet step disclaimer
                return {
                    isVisible: currentStep === CaQuoteFormSteps.PETS.id,
                    disclaimer: (
                        <>
                            <div>
                                <sup>1</sup> 5% multi-pet discount on 2 pets enrolled. 10% discount on 3+ pets enrolled.
                            </div>
                            <div>
                                <sup>2</sup> No purchase necessary. Based on total combined Spot Perks discounts applied to avg. vendor cart value.
                                <a href="https://spotpetinsurance.ca/perks-terms" target="_blank" className="underline">
                                    {" "}
                                    Spotpetinsurance.ca/perks-terms.
                                </a>
                            </div>
                        </>
                    )
                };
            };

            const footerDisclaimers = [getPetStepDisclaimers()];

            const disclaimersToShow = footerDisclaimers.filter(disclaimer => disclaimer.isVisible).map(disclaimer => disclaimer.disclaimer);

            return (
                <React.Fragment>
                    <SiteFooter
                        underwriter={underwriter}
                        formID={FORM_ID}
                        currentStep={currentStep}
                        links={CA_FOOTER_LINKS}
                        content={<FooterContent />}
                        extraContent={disclaimersToShow}
                        copyright={Strings.PTZ_CA.COPYRIGHT_TEXT}
                        className={cn(footerClass)}
                    />
                </React.Fragment>
            );
        }
    };
};
